import React from "react";
import { ArrowRight, Building, Check, Cloud, Database, FileLock2, Fingerprint, HeartHandshake, Hospital, ShieldCheck } from "lucide-react";
import Reveal from "./Reveal";

const AUDIENCES = [
  {
    icon: HeartHandshake,
    label: "Clinics & polyclinics",
    title: "Look bigger. Work lighter.",
    points: [
      "Online booking and video visits that bring patients back",
      "Prescriptions and bills in under a minute per patient",
      "A small team can run front desk, pharmacy and billing",
      "Start with one doctor and grow to many locations",
    ],
    gradient: "from-care-500/15 to-care-500/0",
    iconClass: "bg-care-500",
  },
  {
    icon: Hospital,
    label: "Hospitals & networks",
    title: "Every department on the same page.",
    points: [
      "Live bed occupancy across wards and buildings",
      "IPD charges captured daily — no revenue lost at discharge",
      "Role-based access for doctors, nurses, billing and admins",
      "Network-wide view of patients, locations and teams",
    ],
    gradient: "from-brand-500/15 to-brand-500/0",
    iconClass: "bg-brand-600",
  },
];

const SECURITY = [
  { icon: Fingerprint, title: "OTP sign-in", body: "Password-less login for staff and patients, with a separate session per portal." },
  { icon: FileLock2, title: "Role-based access", body: "Granular view, create and edit permissions per module and per location." },
  { icon: Database, title: "Standards-based records", body: "HL7 FHIR R4 resources and SNOMED CT coding keep data portable and interoperable." },
  { icon: ShieldCheck, title: "ABDM integration", body: "ABHA, HFR and HPR support for India's national digital health ecosystem." },
  { icon: Building, title: "Tenant isolation", body: "Every hospital's data is separated — staff only ever see their own organisation." },
  { icon: Cloud, title: "Cloud native", body: "Nothing to install. Works on any modern browser, on desktop, tablet or phone." },
];

const Benefits = () => (
  <>
    <section className="py-24 sm:py-28">
      <div className="container-x">
        <Reveal className="mx-auto max-w-2xl text-center">
          <span className="eyebrow">Made for your size</span>
          <h2 className="section-title">Whether you run one clinic or twenty hospitals</h2>
        </Reveal>
        <div className="mt-14 grid gap-6 lg:grid-cols-2">
          {AUDIENCES.map((a, i) => (
            <Reveal key={a.label} delay={i * 0.1} className={`relative overflow-hidden rounded-3xl border border-slate-200 bg-gradient-to-b ${a.gradient} p-8 sm:p-10`}>
              <span className={`flex h-12 w-12 items-center justify-center rounded-2xl text-white ${a.iconClass}`}>
                <a.icon className="h-6 w-6" />
              </span>
              <p className="mt-6 text-sm font-semibold uppercase tracking-wider text-slate-500">{a.label}</p>
              <h3 className="mt-2 text-2xl font-bold sm:text-3xl">{a.title}</h3>
              <ul className="mt-6 space-y-3">
                {a.points.map((p) => (
                  <li key={p} className="flex items-start gap-3 text-slate-700">
                    <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-care-500" /> {p}
                  </li>
                ))}
              </ul>
              <a href="#contact" className="mt-8 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-700 hover:gap-2.5 hover:text-brand-800">
                See it for your {i === 0 ? "clinic" : "hospital"} <ArrowRight className="h-4 w-4 transition-all" />
              </a>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section id="security" className="border-y border-slate-200 bg-slate-50/70 py-24 sm:py-28">
      <div className="container-x grid gap-12 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:items-center">
        <Reveal>
          <span className="eyebrow">Security & compliance</span>
          <h2 className="section-title">Patient data deserves the highest care</h2>
          <p className="mt-4 text-lg text-slate-600">
            Health Frigate is built on open healthcare standards and India's digital health stack, with access controls designed for real clinical teams.
          </p>
          <div className="mt-8 flex flex-wrap gap-2">
            {["ABDM", "FHIR R4", "SNOMED CT", "HFR / HPR", "GST"].map((t) => (
              <span key={t} className="rounded-full border border-slate-300 bg-white px-3 py-1 text-xs font-semibold text-slate-700">{t}</span>
            ))}
          </div>
        </Reveal>
        <div className="grid gap-4 sm:grid-cols-2">
          {SECURITY.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.05} className="rounded-2xl border border-slate-200 bg-white p-5">
              <s.icon className="h-5 w-5 text-brand-600" />
              <h3 className="mt-3 text-base font-bold">{s.title}</h3>
              <p className="mt-1.5 text-sm text-slate-600">{s.body}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  </>
);

export default Benefits;
